import React from 'react';

import {
    FormattedHTMLMessage,
    injectIntl,
} from 'react-intl';

import { produce } from 'immer';

import { VerificationService,
         StudentPersonalInfoViewModel,
         FieldValidationErrors,
         ExtendedFieldValidationErrors,
         FormSelectChoice,
         Country,
         Metadata,
         InputSelectOnKeyDown,
         Organization,
         isFormFilled,
         EmailComponent,
         FormFooterComponent,
         CollegeNameComponent,
         setFocus,
         updateViewModelOrganization,
         updateFieldValidationErrorsByFieldId,
         handleEmailOnKeyDown,
         submitForm,
         VerificationStepsEnum,
         setRef,
         BirthDateComponent,
         getConfiguredCountries,
         LastNameComponent,
         HowDoesVerifyingWorkComponent,
         FirstNameComponent,
         ViewModel,
         MarketConsentOptions,
         getMarketConsent,
         CountryComponent,
         FieldId,
         PoweredByComponent,
         OptInComponent,
} from '@sheerid/jslib';
import { GraduationDateComponent as GraduationDate } from '../FormFields/GraduationDate/GraduationDateComponent';
import { TermsAndConditionsComponent } from '../FormFields/TermsAndConditions/TermsAndConditionsComponent';

import _ from 'lodash';

interface StepStudentPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

interface MarketConsentWrapperProps {
    verificationService: VerificationService,
    isErrored: boolean,
    onChange: (value: any) => void,
    viewModel: ViewModel,
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const countryIndia: FormSelectChoice<Country, string> = { value: "IN", label: "India" };

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateCountryChoice = (countryChoice: FormSelectChoice<Country, string>) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            draft.countryChoice = countryChoice;
        });
        verificationService.updateViewModel(nextState);
    };

    const availableCountries: Country[] = getConfiguredCountries(verificationService.programTheme);
    const countryChoices: FormSelectChoice<Country, string>[] = availableCountries
        .map(countryCode => ({
            value: countryCode,
            label: intl.formatMessage({
                id: `countries.${countryCode}`,
                defaultMessage: countryCode,
            }),
        }));

    if (!viewModel.countryChoice && availableCountries.length === 1) {
        updateCountryChoice(countryChoices[0]);
    }

    const handleCountryOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    const MarketConsentWrapperComponent = ({
        verificationService,
        isErrored,
        onChange,
        viewModel,
    }: MarketConsentWrapperProps) => {
        const { programTheme } = verificationService;
        const marketConsentConfig: MarketConsentOptions = getMarketConsent(programTheme);

        return marketConsentConfig.enabled ?
                <OptInComponent
                    isErrored={isErrored}
                    isRequired={marketConsentConfig.required}
                    verificationService={verificationService}
                    value={viewModel.metadata?.marketConsentValue || false}
                    onChange={value => onChange(value)}
                />
                : null;
    };

    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-header__title">
                    <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Claim your student price" />
                </div>

                <div className="sid-header__subtitle">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a student at a degree-granting college or university." />
                    &nbsp;
                    <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                </div>
            </div>

            {
                countryChoices.length > 1 ?
                    <CountryComponent
                        options={countryChoices}
                        value={viewModel.countryChoice?.label ? viewModel.countryChoice : null}
                        isErrored={false}
                        placeholder="Country"
                        onKeyDown={event => handleCountryOnKeyDown(event)}
                        onChange={(countryChoice: FormSelectChoice<Country, string>) => {
                            updateCountryChoice(countryChoice);
                            if (countryChoice && countryChoice.value) {
                                setFocus('organization');
                            }
                        }}
                    />
                : null
            }

            {/* Adding pop up FAQ text when Org is India  */}
            {(_.isEqual(viewModel.countryChoice, countryIndia)) ?
                <div className="sid-india-difficulty-finding-university sid-header__subtitle">
                    <FormattedHTMLMessage
                        id="step.personalInfo.indiaText"
                        defaultMessage= "Difficulty finding your University? "
                    />
                    <a href="https://services.sheerid.com/verify/5d40b8efbe43d4240b2120ad/" target="_blank" rel="noopener noreferrer" className="sid-link">
                        <FormattedHTMLMessage id="step.personalInfo.indiaLink" defaultMessage="Click here" />
                    </a>
                </div>
            :
                null
            }

            <CollegeNameComponent
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder="University / College Name / School Name"
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />


            <div className="sid-names">
                <FirstNameComponent
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder="First Name"
                    onChange={
                        (newValue) => {
                            updateStudentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastNameComponent
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder="Last Name"
                    onChange={
                        (newValue) => {
                            updateStudentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDateComponent
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <GraduationDate
                value={(viewModel.metadata && viewModel.metadata.hasOwnProperty('graduationDate') ? viewModel.metadata.graduationDate : '') as any}
                isErrored={!!fieldValidationErrors.graduationDate}
                errorId={fieldValidationErrors.graduationDate as any}
                intl={intl}
                onChange={(newValue: string) => {
                    const gradParts = newValue.split('-');
                    const gradMonth = gradParts[1];
                    const gradYear = gradParts[0];
                    updateStudentViewModel('metadata', {...verificationService.viewModel.metadata, 'graduationDate': newValue, 'graduationMonth': gradMonth, 'graduationYear': gradYear});
                    updateFieldValidationErrorsByFieldId('graduationDate' as FieldId, newValue, verificationService);
                    }
                }
            />
            {console.log(viewModel.metadata)}

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={'Please use your private email address to receive discounted offers after graduation'}
                placeholder="Email Address"
                onChange={
                    (newValue) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />

            <MarketConsentWrapperComponent
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <TermsAndConditionsComponent
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.termsAndConditions}
                onChange={(newValue) => {
                    updateStudentViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        termsAndConditions: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('termsAndConditions', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        <FormattedHTMLMessage id="verifyMyStudentStatus" defaultMessage="Verify My Student Status" />
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <PoweredByComponent verificationService={verificationService} />
            </FormFooterComponent>
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
